/**
 * ResourceRegistryRail — Rails Phase 2 (DEV-1375) adopter of the shared Rail.
 *
 * Promotes the registered-resources registry to the 'list' Rail; the
 * Resources page main area becomes the needs/matching column only. Selecting
 * a resource here re-ranks the page's needs via the shared userResourcesStore
 * (it does NOT open a Tray — the Add/Edit Tray stays on the registry's own
 * affordance). POLICY_NAVIGATION_RAILS.md §4 (Resources, Phase 2).
 *
 * Mirrors CommunitySwitcherRail: ALL hooks run unconditionally, visibility is
 * gated AFTER (the Rail is mounted app-wide); the data fetch short-circuits
 * off-section; collapse is controlled + persisted.
 *
 * Responsive: below the shared --rail-breakpoint (960px) a 'list' Rail
 * collapses to a 56px strip that can't render the registry, so the Rail
 * yields and ResourcesPage hosts the registry in-page instead (single
 * registry instance, breakpoint-gated).
 */
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { usePersistentCollapse } from '../../hooks/usePersistentCollapse';
import { useIsNarrow } from '../../hooks/useIsNarrow';
import { loadUserResources } from '../../Data/UserResources/userResourcesStore';
import { ConnectedUserResourceRegistry } from '../UserResourceRegistry/ConnectedUserResourceRegistry';
import Rail from './Rail';
import styles from './ResourceRegistryRail.module.css';

const COLLAPSE_KEY = 'l4e_rail_resources_collapsed';

export interface ResourceRegistryRailProps {
  currentUserGuid: string;
}

export default function ResourceRegistryRail({ currentUserGuid }: ResourceRegistryRailProps) {
  const { t } = useAppTranslation('userProfile');
  const location = useLocation();
  const isNarrow = useIsNarrow(960);
  const { collapsed, toggleCollapsed } = usePersistentCollapse(COLLAPSE_KEY, false);

  const onSection =
    location.pathname === '/resources' || location.pathname.startsWith('/resources/');

  // Off-section short-circuit: only fetch when the Rail actually hosts the
  // registry (on /resources, wide screen). Empty userGuid → store no-ops, so
  // the app-wide mount doesn't fetch on every route.
  useEffect(() => {
    void loadUserResources(onSection && !isNarrow ? currentUserGuid : '');
  }, [onSection, isNarrow, currentUserGuid]);

  // Visibility gates (after hooks — hooks must run unconditionally).
  if (!onSection) return null;
  // Narrow screens: ResourcesPage hosts the registry in-page instead.
  if (isNarrow) return null;

  return (
    <Rail
      ariaLabel={t('resources.railAriaLabel', 'My resources')}
      widthVariant="list"
      collapsed={collapsed}
      onToggleCollapsed={() => toggleCollapsed()}
    >
      {collapsed ? (
        // Collapsed strip — a rotated "Resources" label (bottom-to-top),
        // mirroring Discover's ActivityDrawer. Clicking it expands the Rail.
        <button
          type="button"
          className={styles.collapsedStrip}
          onClick={() => toggleCollapsed()}
          aria-label={t('resources.railExpand', 'Expand resources')}
        >
          <span className={styles.collapsedLabel}>{t('resources.railTitle', 'Resources')}</span>
        </button>
      ) : (
        <ConnectedUserResourceRegistry hideHeader currentUserGuid={currentUserGuid} />
      )}
    </Rail>
  );
}
